import { useEffect, useRef } from "react";
import { describeError } from "../../api/client";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { useQuery } from "../../state/useQuery";
import type { AdminTemplatePreview } from "../../api/types";
import { getTemplatePreview } from "./api";
import { TemplatePreviewDialog } from "./TemplatePreviewDialog";

/**
 * Fetches a template's rendered preview and hands it to `TemplatePreviewDialog`
 * (REDISENO-PANEL.md §8.1: card footer "Vista previa" button). Kept as its
 * own small wrapper — `TemplatesScreen.tsx` renders the card grid, never a
 * sandboxed frame directly (`sourceInvariants.test.ts`'s own invariant); the
 * untrusted preview HTML only ever reaches the sandboxed frame inside
 * `TemplatePreviewDialog`, same single-sandboxed-surface contract
 * `TemplateDetailScreen.tsx` already relies on.
 */
export function TemplatePreviewModal({
  templateId,
  templateName,
  onClose,
}: {
  templateId: string;
  templateName: string;
  onClose: () => void;
}) {
  const { data, error, loading, stale, refetch } = useQuery<AdminTemplatePreview>({
    key: ["template-preview", templateId],
    queryFn: () => getTemplatePreview(templateId),
  });

  // adv2-slice08 (id 4377321286): while the preview request is loading or has
  // failed, this branch rendered a raw `aria-modal` div with none of the
  // focus lifecycle `TemplatePreviewDialog` gets once data arrives — focus
  // never moved into the dialog, Tab could escape it, and Escape did nothing.
  // Mirrors `ConfirmDialog`'s established minimal focus-trap contract
  // (mount-focus + boundary Tab-trap + Escape + focus-restoration) rather
  // than the shared `<Modal>` primitive, since this branch keeps this
  // dialog's own dedicated `.v2-template-preview-dialog*` chrome (not
  // `.v2-modal`'s). Scoped to `[data]`: it only (re)binds on the
  // loading<->loaded transition, never on every loading/error toggle within
  // this same not-yet-loaded state (e.g. clicking "Reintentar"), which would
  // otherwise yank focus away from whatever the operator just interacted with.
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Native advisory (TemplatePreviewModal.tsx:~49): captured during RENDER
  // (guarded by a null check), same "Codex review, round 4" contract
  // `TemplatePreviewDialog` itself established for the same reason — this is
  // the ORIGINAL external trigger that opened this modal, read before either
  // this component's own loading Cerrar button or `TemplatePreviewDialog`'s
  // own Cerrar button ever steals focus. Passed straight to
  // `TemplatePreviewDialog` once data arrives below: letting THAT component
  // re-derive its own restore target via `document.activeElement` at that
  // exact transition would grab THIS component's own loading Cerrar button
  // instead (still focused and still in the DOM at render time, even though
  // it's about to be unmounted) — a `.focus()` call on that now-detached
  // node on the loaded dialog's eventual close is a silent no-op, so closing
  // it never actually restored focus to the real trigger at all.
  const externallyFocusedRef = useRef<HTMLElement | null>(null);
  if (externallyFocusedRef.current === null) {
    externallyFocusedRef.current = document.activeElement as HTMLElement | null;
  }

  // Latest `data`, read from this effect's CLEANUP (where `data` itself is
  // still the stale pre-transition value closed over at setup time).
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    if (data) return undefined;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key === "Tab" && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Only restore focus to the external trigger when THIS cleanup fires
      // because the modal is going away without ever loading (Escape/close
      // while loading or after an error). When it instead fires because
      // `data` just arrived, `TemplatePreviewDialog` takes over focus
      // management (including its OWN restore-on-close, using
      // `externallyFocusedRef` above) — restoring here too would yank focus
      // OUT to the trigger for one tick, only for the dialog's own mount
      // effect to immediately move it back in.
      if (!dataRef.current) {
        externallyFocusedRef.current?.focus();
      }
    };
  }, [data]);

  if (data) {
    return (
      <TemplatePreviewDialog
        title={`Vista previa — ${templateName}`}
        html={data.html}
        onClose={onClose}
        restoreFocusTo={externallyFocusedRef.current}
        stale={stale}
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="v2-template-preview-dialog-backdrop">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Vista previa — ${templateName}`}
        className="v2-template-preview-dialog"
      >
        <header className="v2-template-preview-dialog__header">
          <h2>Vista previa — {templateName}</h2>
          <button
            type="button"
            className="v2-template-preview-dialog__close"
            aria-label="Cerrar"
            onClick={onClose}
            ref={closeButtonRef}
          >
            ×
          </button>
        </header>
        <div className="v2-template-preview-dialog__stage">
          {loading && <p role="status">Cargando vista previa…</p>}
          {!loading && error !== undefined && (
            <FeedbackPanel variant="error" message={describeError(error, "No se pudo cargar la vista previa.")} onRetry={refetch} />
          )}
        </div>
      </div>
    </div>
  );
}
